import usePermissions from '@/hooks/usePermissions';
import { getLandingPath } from '@/utils/permissionRegistry';
import { getEncryptedClientId } from '@/utils/helper';
import { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';

interface RequirePermissionProps {
  /** All of these must be held. */
  permission: string | string[];
  children: ReactNode;
}

/**
 * Route guard on permissions. Replaces SuperOwnerRoute / AdminRoute.
 *
 * Renders nothing while the permission set is still resolving, rather than
 * redirecting — bouncing the user to a default route on a slow fetch is far
 * more disruptive than a brief blank frame.
 */
/**
 * Where to send someone who cannot open the page they asked for: the first
 * screen their own permissions do reach. Falling back to '/' bounced them to
 * a landing page they often could not open either.
 */
const deniedPath = (): string => {
  const path = getLandingPath();
  if (!path) return '/';
  const client = getEncryptedClientId();
  return client ? `/${client}${path}` : path;
};

const RequirePermission = ({ permission, children }: RequirePermissionProps) => {
  const { can, isLoading } = usePermissions();

  if (isLoading) return null;

  const required = Array.isArray(permission) ? permission : [permission];
  return can(...required) ? <>{children}</> : <Navigate to={deniedPath()} replace />;
};

export default RequirePermission;
